import { useIntl, FormattedMessage } from 'react-intl'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Datepicker from 'components/shared/Datepicker'
import Info from 'components/shared/Info'
import Loader from 'components/shared/Loader'
import { DatepickerWrapper } from './styles'
import { OriginalSettings } from 'routes/Upsell/Bus/fields/BusDatesAndHours'
import { useApp } from 'store/steps'
import { getDateAndDayNameBrackets } from 'helpers/dates'
import CabanasCalendarModal from 'components/global/CalendarModal/forks/CabanasCalendarModal'
import { useEffect, useState } from 'react'
import {
    useGetCalendarByProductTypeQuery,
    useGetCreatorCabanaLimitByDateQuery,
} from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import moment from 'moment'
import { setInitialData } from 'store/cabana-slice'
import { DefaultTheme } from 'styled-components'

interface CabanaDateProps {
    cabanasQuantity: number
    date: string
    onCalendarOpen: () => void
    onDateChange: (value: string) => void
    onLimitChange: (value: number) => void
    isError: boolean
    isConflict: boolean
    errorMsg: string | null
    isFetching: boolean
    originalSettings?: OriginalSettings
    setError?: (errorType: 'calendar' | 'dates' | 'holidays' | null) => void
    theme?: DefaultTheme
    cabanaLimit: number
    index: number
    testIdPrefix?: string
}

export const CabanaDate = ({
    date,
    onDateChange,
    onLimitChange,
    isError,
    errorMsg,
    setError,
    theme,
    cabanaLimit,
    index,
    testIdPrefix,
}: CabanaDateProps) => {
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { language } = useApp()

    const { defaultDate } = useAppSelector((state) => state.cabana)

    const [isCalendarModalOpened, setIsCalendarModalOpened] = useState(false)

    const hasDarkMode = theme?.mode === 'dark'
    const isClosingToLimit = cabanaLimit <= 3
    const isNoSeats = cabanaLimit === 0

    const {
        data: calendarData,
        isFetching: isCalendarFetching,
        isError: isCalendarError,
    } = useGetCalendarByProductTypeQuery({
        _locale: language,
        productType: 'cabana',
    })

    const {
        data: dayLimitData,
        isFetching: isDayLimitFetching,
        refetch: dayLimitRefetch,
    } = useGetCreatorCabanaLimitByDateQuery(
        {
            _locale: language,
            date,
        },
        {
            skip: !date,
        }
    )

    const ErrorMsg = (msg: string) => <Info color="error">{msg}</Info>

    const handleCalendarModalOpen = () => setIsCalendarModalOpened(true)
    const handleCalendarModalClose = () => setIsCalendarModalOpened(false)
    const handleChangeDate = (date: string) => onDateChange(date)

    const verifyDefaultDate = () => {
        if (calendarData && defaultDate) {
            const { from, to, excluded } = calendarData
            if (
                moment(defaultDate) < moment(from) ||
                moment(defaultDate) > moment(to) ||
                excluded?.includes(defaultDate)
            ) {
                dispatch(setInitialData({ date: '' }))
            }
        }
    }

    useEffect(() => {
        !isCalendarFetching && verifyDefaultDate()
    }, [isCalendarFetching])

    useEffect(() => {
        if (!isDayLimitFetching && dayLimitData) {
            const limit = dayLimitData?.limit || 0
            onLimitChange(limit)
        }
    }, [isDayLimitFetching])

    useEffect(() => {
        dayLimitRefetch()
    }, [date])

    useEffect(() => {
        verifyDefaultDate()
    }, [])

    return (
        <Box gap={24} isFullWidth>
            <Text
                variant="heading4"
                color={hasDarkMode ? 'grayscale_200' : undefined}
            >
                <FormattedMessage
                    id="global.input.reservation_date_label"
                    defaultMessage="Termin rezerwacji"
                />
            </Text>
            <Box
                gap={16}
                mt={[8, 24]}
                direction={['column', 'row']}
                align="center"
                isFullWidth
            >
                <DatepickerWrapper isFullWidth>
                    <Datepicker
                        id={'cabanaDate'}
                        value={date ? [date] : []}
                        openCalendar={handleCalendarModalOpen}
                        placeholder={formatMessage({
                            id: 'global.input.choose_date_label',
                            defaultMessage: 'Wybierz datę',
                        })}
                        customFormatter={getDateAndDayNameBrackets}
                        theme={theme}
                        testId={`${
                            testIdPrefix ?? 'cabana'
                        }-${index}-trigger-reservation-date`}
                    />
                </DatepickerWrapper>
                {isClosingToLimit && (
                    <Box mt={[8, 0]} isFullWidth>
                        <Text
                            variant="bodySmall"
                            color={hasDarkMode ? 'warning' : 'error'}
                        >
                            {isNoSeats ? (
                                <FormattedMessage
                                    id="upsells.cabanas.msg_no_more_seats"
                                    defaultMessage="W tym dniu nie ma już wolnych caban. Wybierz inną datę."
                                />
                            ) : (
                                <FormattedMessage
                                    id="upsells.cabanas.limit_warning"
                                    defaultMessage="Zostały tylko {quantity} cabany na ten dzień!"
                                    values={{
                                        quantity: cabanaLimit,
                                    }}
                                />
                            )}
                        </Text>
                    </Box>
                )}
            </Box>

            {isError &&
                (errorMsg ? (
                    ErrorMsg(errorMsg)
                ) : (
                    <Loader isFullWidth align="flex-start" />
                ))}

            <CabanasCalendarModal
                isOpened={isCalendarModalOpened}
                close={handleCalendarModalClose}
                dateHandler={handleChangeDate}
                initialDate={date}
                errorHandler={(isError: boolean) => {
                    setError && setError(isError ? 'calendar' : null)
                }}
                data={calendarData}
                isFetching={isCalendarFetching}
                isError={isCalendarError}
            />
        </Box>
    )
}
